import type {
  GenericObject,
  PayloadDraftEntry,
  UUID,
} from '@bizzdesign/modeler/models';
import type {
  RepositoryLoadedElementData,
  RepositoryLoadedElementIdPair,
} from '@bizzdesign/shared/models';
import { getPayloadBasedOnElementKind } from './payload.utils';

export function getFromRepoSemanticAndGraphicPairs(
  elements: GenericObject[],
  fromRepoIds: string[],
): RepositoryLoadedElementIdPair[] {
  const pairs: RepositoryLoadedElementIdPair[] = [];
  const graphics = elements.filter(
    (element) => element['elementKind'] === 'diagram',
  );
  for (const graphic of graphics) {
    const semanticId = fromRepoIds.find((id) => graphic['semanticsId'] === id);
    if (!semanticId) {
      // if there isn't a graphic, chances are the graphic was removed
      console.warn(
        "getFromRepoSemanticAndGraphicPairs: a referenced element doesn't have a graphic! ID:",
        graphic['semanticsId'],
      );
      continue;
    }
    pairs.push({
      graphicId: graphic['id'],
      semanticId: semanticId,
    });
  }

  return pairs;
}

export function getFromRepoElementsIdsFromPromotion(
  payload?: GenericObject[] | null,
): RepositoryLoadedElementIdPair[] {
  if (!payload) {
    return [];
  }

  return payload
    .filter((e) => !!e['promotion']?.['semanticsId'])
    .map((e) => ({
      semanticId: e['promotion']['semanticsId'],
      graphicId: e['id'],
    }));
}

export function getLoadedFromRepoElementsData(
  repoPairs: RepositoryLoadedElementIdPair[],
  elements: GenericObject[],
  isFreeFormat: boolean,
): RepositoryLoadedElementData[] {
  return repoPairs.map<RepositoryLoadedElementData>((pair) => {
    const idForName = isFreeFormat ? pair.graphicId : pair.semanticId;
    const name =
      elements.find((element) => element['id'] === idForName)?.['name']?.[
        'en'
      ] ?? '';
    const graphic = elements.find(
      (element) => element['id'] === pair.graphicId,
    );
    const externalData = graphic?.['metadata']?.['external'];
    return {
      ...pair,
      name,
      externalData,
    };
  });
}

export function hasPromotionSemanticsId(object: GenericObject): boolean {
  return !!object['promotion']?.['semanticsId'];
}

export function hasPromotionSemanticsIdByGraphicId(
  objects: GenericObject[],
  id: string,
): boolean | undefined {
  const obj = objects.find((object) => object['id'] === id);
  if (!obj) return;

  return hasPromotionSemanticsId(obj);
}

export function isElementReusedFromRepo(
  diagramElementData?: GenericObject,
): boolean {
  if (!diagramElementData) return false;

  return !!diagramElementData['fromRepo'];
}

function filterReusedArrayBasedOnSemanticsOnly(
  semanticData: PayloadDraftEntry,
  reusedElements: string[],
): string[] {
  const semanticKeys = Object.keys(semanticData);
  return reusedElements.filter((el) => semanticKeys.includes(el));
}

export function clearReusedArrayOfRedundantIds(
  payload: PayloadDraftEntry,
  reusedElements: string[],
): string[] {
  const viewSemanticContent = getPayloadBasedOnElementKind(payload, 'model');
  return filterReusedArrayBasedOnSemanticsOnly(
    viewSemanticContent,
    reusedElements,
  );
}

export function mapFromRepoPairsToApiIds(
  fromRepoArray: RepositoryLoadedElementIdPair[],
): UUID[] {
  return fromRepoArray.map((ids) => ids.semanticId) as UUID[];
}
